<template>
	<div class="block-editor-container subflow-block-editor display-flex flex-column">
		<StepDetailsHeader
			v-on="$listeners"
			hideActionText
			:icon="subflowServiceSlug"
			:hideProfileSelectionOption="!isProfileSelectionNeeded"
			:isContextualNode="false"
			:showDropdownMenu="false"
		/>
		<div class="editor-body flex-stretch-to-scroll pa-4 font-size-md">
			<div class="font-weight-600">Input fields</div>
			<div class="mt-2">
				Input fields let you send data from parent features into the subflow. The fields you add below will appear as inputs when you add
				this subflow as a step in a parent feature.
			</div>
			<FieldsDefinitionBuilder
				class="mt-4"
				fieldTitleText="Input field name"
				buttonText="+ Add input field"
				fieldTypeText="input"
				:fieldsDefinitions="subflowParams"
				:subflowsUsageWorkflowIds="selectedSubflow.sub_flow_parent_workflows"
				:isDisabled="isUpdatingSubflowParams"
				@onFieldsDefChanged="onFieldsDefChanged"
			/>
		</div>

		<BottomSection
			isOfTypeWaitingForTriggerTest
			:testResponseTruncate="testResponseTruncate"
			:isFormValid="isFormValid"
			:isWaitingForSampleData="isWaitingForSampleData(this.selectedSubflow.id)"
		>
			<template slot-scope="{ cancelCallback }">
				<WaitingForSubflowTestFloatingMessage
					:hasParent="selectedSubflow.sub_flow_parent_workflows.length > 0"
					:cancelCallback="cancelCallback"
				/>
			</template>
		</BottomSection>
	</div>
</template>

<script>
// import api from '@/api';

import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { isWaitingForSampleData } from '@/store/modules/workflows/getters';
import { getSubflowParams, isUpdatingSubflowParams } from '@/store/modules/subflows/getters';
import { fetchSubflowParam, updateSubflowParam } from '@/store/modules/subflows/actions';
import { testStep, updateEditNodeTestResultStatus } from '@/store/modules/selectedNode/actions';
import { updateNodeTestResult } from '@/store/modules/nodes/actions';

import BlockEditorBridge from '../block-editor-bridge';

import TestModalService from '../helpers/test-modal-service';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import StepDetailsHeader from '../StepDetailsHeader';
import FieldsDefinitionBuilder from './helpers/FieldsDefinitionBuilder/FieldsDefinitionBuilder';
import WaitingForSubflowTestFloatingMessage from '../helpers/WaitingForSubflowTestFloatingMessage';
import BottomSection from './helpers/BottomSection';
import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'SubflowInputBlockEditor',
	components: {
		StepDetailsHeader,
		FieldsDefinitionBuilder,
		WaitingForSubflowTestFloatingMessage,
		BottomSection,
	},
	props: {
		nodeID: { type: [Number, String] },
		paramsMap: { type: Object },
		hasEditPermissions: { type: Boolean },
	},
	data() {
		return {
			fieldsDefinitions: [],
			isShowParams: null,
			isShowTestResultModal: false,
			isShowAnimation: false,
			isTestedDataChanged: false,
			isBtnFuncLoading: false,
			isChangeAction: false,
			isTriggerSetupLoading: false,
			hasTestParams: false,
			timer: null,
			isCanceledWaitingForSampleData: false,
			isFormValid: true,
		};
	},
	computed: {
		...mapGettersShorthand({ editWorkflow, getSubflowParams, isWaitingForSampleData, isUpdatingSubflowParams }),
		selectedSubflow() {
			return this.editWorkflow;
		},
		subflowInputId() {
			return this.selectedSubflow?.current_draft.sub_flow_input_configuration;
		},
		subflowInput() {
			return this.selectedSubflow && this.getSubflowParams(this.subflowInputId);
		},
		subflowParams() {
			return this.getSubflowParams(this.subflowInputId);
		},
		stepHeaderIcon() {
			return BlockEditorBridge.stepHeaderIcon();
		},
		isProfileSelectionNeeded() {
			return BlockEditorBridge.isProfileSelectionNeeded();
		},
		subflowServiceSlug() {
			return ServiceSlugs.SubFlow;
		},
	},
	methods: {
		...mapActionsShorthand({ fetchSubflowParam, updateSubflowParam, testStep, updateEditNodeTestResultStatus, updateNodeTestResult }),
		hideTestCard() {},
		onFieldsDefChanged(newDefinitions) {
			this.updateSubflowParam({ id: this.subflowInputId, subflowParams: newDefinitions });
			this.updateEditNodeTestResultStatus({ value: TEST_RESULT_STATUSES.UNKNOWN });
			this.updateNodeTestResult({ id: this.nodeID, testResponse: { test_result_status: TEST_RESULT_STATUSES.UNKNOWN } });
		},
		applyTestAgain() {},
		testResponseTruncate(response) {
			const retVal = TestModalService.mapTestResponseToModalView(response, this.subflowInput);
			return [...retVal];
		},
	},
	mounted() {
		this.fetchSubflowParam({ id: this.subflowInputId, forceGet: true });
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';
@import '../block-editor.scss';

.editor-body {
	max-width: 470px;
}
</style>
